import { useEffect, useId, useRef, useState } from 'react';
import { ArrowUpRight, Copy } from 'lucide-react';
import type { RadarLocale } from '@/i18n/radarLanding';
import { useRadarLocale } from '@/i18n/RadarLocaleContext';
import type { MonitoringAdviceResponse } from './monitoringAdvice';
import { monitoringAdviceCopy } from './monitoringAdviceCopy';
import '@/styles/radar-next-action.css';

type Props = {
  companyId: string;
  locale?: RadarLocale;
  /** Change with the Brief or research revision to clear an obsolete card. */
  contextKey?: string;
  loadAdvice: () => Promise<MonitoringAdviceResponse>;
  /** Opens the host page's existing contact flow; this card never sends messages. */
  onOpenContacts?: (contactId?: string) => void;
};

function sourceUrl(value: string) {
  try {
    const url = new URL(value);
    return ['http:', 'https:'].includes(url.protocol) ? url.href : undefined;
  } catch { return undefined; }
}

/** Automatically reads a saved result. Generation runs only in the scheduled worker. */
export function RadarNextActionCard({ companyId, locale: overrideLocale, contextKey = '', loadAdvice, onOpenContacts }: Props) {
  const { locale: preferredLocale } = useRadarLocale();
  const locale = overrideLocale || preferredLocale;
  const text = monitoringAdviceCopy(locale);
  const titleId = useId(), openerId = useId();
  const generation = useRef(0);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [result, setResult] = useState<MonitoringAdviceResponse | null>(null);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');

  // Callback identity changes on host renders must not cause repeated reads.
  const loader = useRef(loadAdvice);
  loader.current = loadAdvice;
  useEffect(() => {
    const request = ++generation.current;
    setState('loading'); setResult(null); setCopyState('idle');
    void (async () => {
      try {
        const response = await loader.current();
        if (request !== generation.current) return;
        if (response.company_id !== companyId || response.schema_version !== 1
            || response.status === 'ready' && !response.advice) throw new Error('Invalid advice response');
        setResult(response); setState('ready');
      } catch {
        if (request === generation.current) { setResult(null); setState('error'); }
      }
    })();
    return () => { generation.current += 1; };
  }, [companyId, locale, contextKey]);

  async function copyMessage() {
    const message = result?.advice?.message;
    if (!message || result?.advice?.action !== 'contact') return;
    const request = generation.current;
    try {
      await navigator.clipboard.writeText(message);
      if (request === generation.current) setCopyState('copied');
    } catch {
      if (request === generation.current) setCopyState('failed');
    }
  }

  const advice = result?.status === 'ready' ? result.advice : null;
  const target = advice?.contact_target;
  const targetUrl = target?.url ? sourceUrl(target.url) : undefined;
  const partial = !!result && !['completed', 'no_change'].includes(result.coverage.check_state || '');
  return <section className="radar-next-action" aria-labelledby={titleId} aria-busy={state === 'loading'}>
    <header><span className="radar-next-action-mark" aria-hidden="true"/><h3 id={titleId}>{text.title}</h3></header>
    {state === 'loading' && <p role="status" className="radar-next-action-intro">{text.loading}</p>}
    {state === 'error' && <p role="alert" className="radar-next-action-error">{text.error}</p>}
    {state === 'ready' && <div aria-live="polite">
      {result?.status === 'pending' && <p>{text.awaiting}</p>}
      {result?.status === 'stale' && <p>{text.stale}</p>}
      {result?.status === 'failed' && <p role="alert">{text.failed}</p>}
      {result?.status === 'insufficient_evidence' ? <p>{text.empty}</p> : advice && <>
        <h4>{advice.headline}</h4><p className="radar-next-action-reason">{advice.rationale}</p>
        {advice.action === 'contact' ? <>
          {target && <div className="radar-next-action-entry">
            <span>{text.start}</span><strong>{target.label}</strong>{target.title && <small>{target.title}</small>}
            <p>{target.kind === 'person' ? text.personNote : target.kind === 'company_email' ? text.mailboxNote : text.websiteNote}</p>
            {targetUrl && <a href={targetUrl} target="_blank" rel="noreferrer">{target.kind === 'person' ? text.openPerson : target.kind === 'company_email' ? text.openMailbox : text.openWebsite}<ArrowUpRight size={13} aria-hidden="true"/></a>}
          </div>}
          <dl><div><dt>{text.role}</dt><dd>{advice.recipient_role}</dd></div>
            <div><dt>{text.goal}</dt><dd>{advice.success_criterion}</dd></div></dl>
          <label className="radar-next-action-opener" htmlFor={openerId}><span>{text.message}</span>
            <textarea id={openerId} readOnly value={advice.message || ''} rows={5}/>
          </label>
          <div className="radar-next-action-buttons">
            <button type="button" className="radar-next-action-primary" aria-live="polite" onClick={copyMessage}><Copy size={15} aria-hidden="true"/>{copyState === 'copied' ? text.copied : text.copy}</button>
            {onOpenContacts && <button type="button" className="radar-next-action-secondary" onClick={() => onOpenContacts(target?.contact_id || undefined)}>{text.contacts}<ArrowUpRight size={15} aria-hidden="true"/></button>}
          </div>
          <div role="status" className="radar-next-action-copy-status">{copyState === 'failed' ? text.copyFailed : ''}</div>
        </> : <dl><div><dt>{text.reconsider}</dt><dd>{advice.revisit_when}</dd></div></dl>}
        {!!advice.evidence.length && <details className="radar-next-action-evidence"><summary>{text.evidence} · {advice.evidence.length}</summary>
          <ul>{advice.evidence.map((source, index) => {
            const url = sourceUrl(source.url);
            return <li key={`${source.event_id}-${index}`}><span>{source.evidence_status === 'confirmed' ? text.confirmed : text.pending}</span>
              <blockquote>{source.quote}</blockquote>{url && <a href={url} target="_blank" rel="noreferrer">{source.title || new URL(url).hostname}<ArrowUpRight size={13} aria-hidden="true"/></a>}
            </li>;
          })}</ul>
        </details>}
      </>}
      {partial && <p className="radar-next-action-check">{text.incomplete}</p>}
      {result?.generated_at && <small className="radar-next-action-updated">{text.prepared} · {new Date(result.generated_at).toLocaleDateString(locale)}</small>}
    </div>}
  </section>;
}
